import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import CustomSelect from '../../ui/CustomSelect';
import { Button } from '../../ui/button';
import { formatRupiah } from '../recruitment/utils/formatRupiah';

export default function OvertimeModal({
  open,
  editing,
  form,
  setForm,
  employees,
  saving,
  onClose,
  onSave,
}) {
  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(`2000-01-01T${start}`);
    const endDate = new Date(`2000-01-01T${end}`);
    let hours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
    if (hours < 0) hours += 24;
    return Math.round(hours * 10) / 10;
  };

  useEffect(() => {
    if (open && !editing && form.start_time && form.end_time) {
      const duration = calculateDuration(form.start_time, form.end_time);
      const rate = parseFloat(form.rate_per_hour || 0);
      const total = Math.round(duration * rate);
      setForm({
        ...form,
        duration_hours: String(duration),
        total_amount: String(total),
      });
    }
  }, [open, form.start_time, form.end_time, form.rate_per_hour]);

  if (!open) return null;

  const handleTimeChange = (field, value) => {
    const newForm = { ...form, [field]: value };

    if (field === 'start_time' || field === 'end_time') {
      const duration = calculateDuration(
        field === 'start_time' ? value : form.start_time,
        field === 'end_time' ? value : form.end_time
      );
      newForm.duration_hours = String(duration);
      const rate = parseFloat(form.rate_per_hour || 0);
      newForm.total_amount = String(Math.round(duration * rate));
    } else if (field === 'duration_hours') {
      const duration = parseFloat(value || 0);
      const rate = parseFloat(form.rate_per_hour || 0);
      newForm.total_amount = String(Math.round(duration * rate));
    } else if (field === 'rate_per_hour') {
      const duration = parseFloat(form.duration_hours || 0);
      const rate = parseFloat(value || 0);
      newForm.total_amount = String(Math.round(duration * rate));
    }

    setForm(newForm);
  };

  const employeeOptions = [
    { value: '', label: 'Pilih pegawai' },
    ...employees.map(emp => ({ value: emp.id, label: `${emp.full_name} (${emp.employee_id})` })),
  ];

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden animate-modal">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{editing ? 'Edit Lembur' : 'Tambah Lembur'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Pegawai <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              value={form.employee_id}
              onChange={val => setForm({ ...form, employee_id: val })}
              options={employeeOptions}
              placeholder="Pilih pegawai"
              width="w-full"
              disabled={!!editing}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Tanggal <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
              disabled={!!editing}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Jam Mulai</label>
              <input
                type="time"
                value={form.start_time}
                onChange={e => handleTimeChange('start_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Jam Selesai</label>
              <input
                type="time"
                value={form.end_time}
                onChange={e => handleTimeChange('end_time', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Durasi (Jam)</label>
              <input
                type="number"
                step="0.1"
                value={form.duration_hours}
                onChange={e => handleTimeChange('duration_hours', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Tarif per Jam</label>
              <input
                type="number"
                value={form.rate_per_hour}
                onChange={e => handleTimeChange('rate_per_hour', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="space-y-1 bg-orange-50 p-4 rounded-lg">
            <label className="text-sm font-medium text-orange-700">Total Bonus Lembur</label>
            <p className="text-2xl font-bold text-orange-600">
              Rp {formatRupiah(form.total_amount || 0)}
            </p>
            <p className="text-xs text-gray-600">
              Formula: {form.duration_hours || 0}h × Rp {formatRupiah(form.rate_per_hour || 0)}
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">Keterangan</label>
            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Alasan lembur"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !form.employee_id || !form.date}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editing ? 'Simpan' : 'Tambah'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
